/**
 * Step 3: Your details
 *
 * Final step for email registration:
 * - Display name input
 * - Email input
 * - CAPTCHA verification
 * - Submit button
 *
 * WP-SEC-27: no password here. Registration does not prove the email address,
 * so a password chosen now could belong to someone who signed up with another
 * person's address. The password is chosen after verification: the link in
 * the verification email leads to "Set your password".
 *
 * WP-AUTH-11: Multi-Step Signup Wizard
 */

import { useState, useCallback, useEffect, useRef, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import CaptchaWidget from '@/components/auth/CaptchaWidget';
import { useWizard } from './WizardContext';
import { useWizardAnalytics } from '@/hooks/useWizardAnalytics';
import { useAuthStore } from '@/store/authStore';
import { getDeviceFingerprint } from '@/utils/fingerprint';
import { clearWizardState } from '@/utils/wizardStorage';

/**
 * Error message mapping for clear UX
 */
const errorMessages = {
  email_already_exists: 'This email is already registered. Try signing in instead.',
  captcha_failed: 'Human verification failed. Please try again.',
  rate_limited: 'Too many attempts. Please wait a moment and try again.',
  network_error: 'Connection error. Please check your internet and try again.',
  server_error: 'Something went wrong. Please try again in a few minutes.',
} as const;

/**
 * Parse API error to user-friendly message
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Check for known error patterns
    if (message.includes('already') || message.includes('exist')) {
      return errorMessages.email_already_exists;
    }
    if (message.includes('rate') || message.includes('too many')) {
      return errorMessages.rate_limited;
    }
    if (message.includes('captcha') || message.includes('verification')) {
      return errorMessages.captcha_failed;
    }
    if (message.includes('network') || message.includes('fetch')) {
      return errorMessages.network_error;
    }

    return error.message;
  }

  return errorMessages.server_error;
}

export interface Step3CredentialsProps {
  /** Callback when registration is complete */
  onComplete: (email: string) => void;
  /** Callback to go back to previous step */
  onBack: () => void;
}

export default function Step3Credentials({ onComplete, onBack }: Step3CredentialsProps) {
  const { state, setError, clearError, setSubmitting } = useWizard();
  const { register } = useAuthStore();
  const analytics = useWizardAnalytics();

  // Local form state
  const [name, setName] = useState(state.name);
  const [email, setEmail] = useState(state.email);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState(false);

  // Device fingerprint for MFA device trust
  const deviceFingerprintRef = useRef<string | null>(null);

  // Initialize device fingerprint on mount
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        deviceFingerprintRef.current = await getDeviceFingerprint();
      } catch {
        // Fingerprint collection is best-effort
      }
    };
    initFingerprint();
  }, []);

  // Track step view
  useEffect(() => {
    analytics.trackStepViewed(3, 'Credentials');
    analytics.trackCaptchaShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { error, isSubmitting, gdprConsent, marketingConsent } = state;

  // Can submit when all fields are filled and CAPTCHA is verified
  const canSubmit =
    name.trim() !== '' &&
    email.trim() !== '' &&
    captchaToken !== null &&
    !isSubmitting;

  /**
   * Handle CAPTCHA verification success
   */
  const handleCaptchaVerify = useCallback(
    (token: string) => {
      setCaptchaToken(token);
      setCaptchaError(false);
      analytics.trackCaptchaPassed();
    },
    [analytics]
  );

  /**
   * Handle CAPTCHA error
   */
  const handleCaptchaError = useCallback(() => {
    setCaptchaToken(null);
    setCaptchaError(true);
    analytics.trackCaptchaFailed();
  }, [analytics]);

  /**
   * Handle CAPTCHA token expiry
   */
  const handleCaptchaExpire = useCallback(() => {
    setCaptchaToken(null);
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      clearError();

      // Require CAPTCHA
      if (!captchaToken) {
        setError('Please complete the human verification');
        analytics.trackValidationError('captcha_required', 'captcha');
        return;
      }

      setSubmitting(true);
      analytics.trackSubmitted('email');

      try {
        // Pass consent data to registration
        await register(email, name, gdprConsent, marketingConsent);

        // Success
        analytics.trackCompleted('email');
        clearWizardState();
        onComplete(email);
      } catch (err) {
        const errorMessage = getErrorMessage(err);
        setError(errorMessage);
        analytics.trackError('registration_failed');

        // Check for specific errors to track
        if (errorMessage.includes('already registered')) {
          analytics.trackValidationError('email_exists', 'email');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      name,
      email,
      captchaToken,
      gdprConsent,
      marketingConsent,
      register,
      setError,
      clearError,
      setSubmitting,
      analytics,
      onComplete,
    ]
  );

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    analytics.trackStepBack(3, 2);
    onBack();
  }, [analytics, onBack]);

  return (
    <div id="wizard-content">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Step heading */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
          <p className="mt-1 text-sm text-gray-600">
            Enter your details. We&apos;ll email you a link to confirm your address, and
            you&apos;ll choose your password from there.
          </p>
        </div>

        {/* Error display */}
        {error && (
          <Alert variant="error" onDismiss={clearError}>
            {error}
            {error.includes('already registered') && (
              <span className="block mt-1">
                <Link
                  to="/login"
                  className="font-medium text-red-800 underline hover:text-red-900"
                >
                  Sign in instead
                </Link>
              </span>
            )}
          </Alert>
        )}

        {/* Name input */}
        <Input
          label="Display name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Alex, FamilyAdmin"
          autoComplete="name"
          autoFocus
          required
          disabled={isSubmitting}
          hint="For privacy, consider using a nickname instead of your real name"
        />

        {/* Email input */}
        <Input
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          disabled={isSubmitting}
        />

        {/* CAPTCHA */}
        <div className="pt-2">
          <p className="text-sm font-medium text-gray-700 mb-2">Human Verification</p>
          <CaptchaWidget
            onVerify={handleCaptchaVerify}
            onError={handleCaptchaError}
            onExpire={handleCaptchaExpire}
            disabled={isSubmitting}
          />
          {captchaError && (
            <p className="mt-2 text-sm text-red-600" role="alert">
              Verification failed. Please try again.
            </p>
          )}
        </div>

        {/* Navigation */}
        <div className="pt-4 flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting}
            className="flex-1"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit}
            isLoading={isSubmitting}
            className="flex-1"
          >
            Create Account
          </Button>
        </div>
      </form>
    </div>
  );
}
